import { getAllSceneNodes } from "@/document/model/document";
import type { CurvaDocument } from "@/document/model/document";
import type { VectorVertex } from "@/document/model/vectorNetwork";
import { nodeSelectionPointToWorld } from "@/document/operations/nodeWorldTransform";
import type { ObjectSnapGuide } from "./objectSnap";

export function snapVectorPointToGeometry(
  vertices: VectorVertex[],
  draggedVertexId: string,
  point: { x: number; y: number },
  zoom: number,
): { point: { x: number; y: number }; guides: ObjectSnapGuide[] } | undefined {
  const tolerance = 5 / Math.max(0.0001, zoom);
  const candidates = vertices.filter(vertex => vertex.id !== draggedVertexId);
  const xSnap = closestCoordinateSnap(point.x, candidates.map(vertex => vertex.x), tolerance);
  const ySnap = closestCoordinateSnap(point.y, candidates.map(vertex => vertex.y), tolerance);
  if (!xSnap && !ySnap) {
    return undefined;
  }

  const snappedPoint = {
    x: xSnap?.value ?? point.x,
    y: ySnap?.value ?? point.y,
  };
  const guides: ObjectSnapGuide[] = [];
  if (xSnap) {
    const alignedVertices = candidates.filter(vertex => Math.abs(vertex.x - xSnap.value) <= 0.0001);
    guides.push({
      orientation: "vertical",
      position: xSnap.value,
      from: Math.min(snappedPoint.y, ...alignedVertices.map(vertex => vertex.y)),
      to: Math.max(snappedPoint.y, ...alignedVertices.map(vertex => vertex.y)),
    });
  }
  if (ySnap) {
    const alignedVertices = candidates.filter(vertex => Math.abs(vertex.y - ySnap.value) <= 0.0001);
    guides.push({
      orientation: "horizontal",
      position: ySnap.value,
      from: Math.min(snappedPoint.x, ...alignedVertices.map(vertex => vertex.x)),
      to: Math.max(snappedPoint.x, ...alignedVertices.map(vertex => vertex.x)),
    });
  }

  return { point: snappedPoint, guides };
}

export function vectorEditGuidesToWorld(document: CurvaDocument, nodeId: string, guides: ObjectSnapGuide[]): ObjectSnapGuide[] {
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    return guides;
  }

  const toWorld = (point: { x: number; y: number }) => nodeSelectionPointToWorld(document, nodeId, point);
  return guides.map(guide => {
    const from = guide.orientation === "vertical"
      ? { x: guide.position, y: guide.from }
      : { x: guide.from, y: guide.position };
    const to = guide.orientation === "vertical"
      ? { x: guide.position, y: guide.to }
      : { x: guide.to, y: guide.position };
    const worldFrom = toWorld(from);
    const worldTo = toWorld(to);
    return { ...guide, x1: worldFrom.x, y1: worldFrom.y, x2: worldTo.x, y2: worldTo.y };
  });
}

function closestCoordinateSnap(value: number, candidates: number[], tolerance: number): { value: number; distance: number } | undefined {
  return candidates.reduce<{ value: number; distance: number } | undefined>((best, candidate) => {
    const distance = Math.abs(candidate - value);
    if (distance > tolerance || (best && distance >= best.distance)) {
      return best;
    }
    return { value: candidate, distance };
  }, undefined);
}
